import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useApolloClient } from '@apollo/client';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { getClockOutModalOpen } from 'lib/selectors/getClockOutModalOpen';
import { getClockOutOperator } from 'lib/selectors/getClockOutOperator';
import { getCurrentOperator } from 'lib/selectors/getCurrentOperator';
import { getCompany } from 'lib/selectors/getCompany';
import { getShiftHandoverEnabled } from 'lib/selectors/getShiftHandoverEnabled';
import { getShiftHandoverTimesheetRef } from 'lib/selectors/getShiftHandoverTimesheetRef';
import { CHECK_OPEN_LABOR_TICKETS } from 'lib/api/queries';
import { CLOCK_OUT_OPERATOR } from 'lib/api/mutations';
import { throwSuccessToast, throwErrorToast } from 'lib/utils/toast';
import {
  actionCloseClockOutModal,
  actionOpenClockOutFlow,
  actionOpenShiftHandover,
  actionSetShiftHandoverTimesheetRef,
} from 'lib/actions';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(26, 32, 44, 0.2);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0px;
  width: 544px;
  height: 296px;
  background: #ffffff;
  box-shadow: 0px 0px 1px rgba(26, 32, 44, 0.2),
    0px 26px 80px rgba(26, 32, 44, 0.2);
  border-radius: 8px;
`;

const ModalHeader = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 12px 8px 12px 16px;
  gap: 16px;
  width: 544px;
  height: 72px;
  background: #ffffff;
  border-bottom: 1px solid #e2e8f0;
`;

const HeaderText = styled.div`
  width: 456px;
  height: 24px;
  font-family: 'Noto Sans';
  font-style: normal;
  font-weight: 400;
  font-size: 20px;
  line-height: 24px;
  display: flex;
  align-items: center;
  letter-spacing: 0.005em;
  color: #4a5568;
  flex-grow: 1;
`;

const ButtonContainer = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 40px;
  gap: 16px;
  width: 544px;
  height: 224px;
  background: #ffffff;
  border-bottom: 1px solid #cbd5e0;
`;

const Button = styled.button`
  box-sizing: border-box;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 0px 24px;
  width: 464px;
  height: 64px;
  background: #ffffff;
  border: 1px solid #cbd5e0;
  border-radius: 4px;
  font-family: 'Noto Sans';
  font-style: normal;
  font-weight: 500;
  font-size: 20px;
  line-height: 24px;
  text-align: center;
  letter-spacing: 0.005em;
  color: #4a5568;
  cursor: pointer;

  &:hover {
    background: #f7fafc;
  }

  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
`;

export const ClockOutModal = React.memo(() => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const client = useApolloClient();
  const isOpen = useSelector(getClockOutModalOpen);
  const clockOutOperator = useSelector(getClockOutOperator);
  const currentOperator = useSelector(getCurrentOperator);
  const company = useSelector(getCompany);
  const shiftHandoverEnabled = useSelector(getShiftHandoverEnabled);
  const capturedTimesheetRef = useSelector(getShiftHandoverTimesheetRef);
  const [isClockingOut, setIsClockingOut] = useState(false);

  // erpId is the personId the clock-out endpoint keys on. It's stored when the
  // modal opens (actionOpenClockOutModal), with the signed-in operator as fallback.
  const personId = clockOutOperator || currentOperator?.erpId;

  if (!isOpen) return null;

  const handleClose = () => {
    dispatch(actionCloseClockOutModal());
  };

  // Open the multi-ticket gauntlet: the embedded labor-ticket-form drains the
  // operator's remaining open tickets, then shows the "All set!" clock-out button.
  const openClockOutFlow = () => {
    handleClose();
    dispatch(actionOpenClockOutFlow());
  };

  const handleClockOut = async () => {
    if (isClockingOut) return;

    // Without a personId we can't check for remaining work or clock out directly,
    // so fall back to the embedded flow which resolves the operator itself.
    if (!personId) {
      openClockOutFlow();
      return;
    }

    setIsClockingOut(true);

    // The labor ticket for THIS machine is already submitted by the time this
    // modal opens (see useHandleLaborTicketSubmit / useOperatorLogout), so any
    // remaining open ticket means the operator still has work on another machine
    // -> keep the multi-ticket gauntlet. Zero remaining -> this is the operator's
    // last open work, so clock out immediately (single-ticket case).
    let hasOtherOpenWork;
    try {
      const { data } = await client.query({
        query: CHECK_OPEN_LABOR_TICKETS,
        variables: {
          includePeriod: shiftHandoverEnabled,
          periodWhere: {
            companyId: { _eq: company.id },
            personId: { _eq: personId },
            clockOut: { _is_null: true },
          },
          where: {
            personId: { _eq: personId },
            clockOut: { _is_null: true },
          },
        },
        fetchPolicy: 'network-only',
      });
      hasOtherOpenWork = (data?.erpLaborTickets?.length || 0) > 0;
      const timesheetRef = data?.erpTimesheets?.[0]?.timesheetRef;
      if (
        shiftHandoverEnabled &&
        capturedTimesheetRef == null &&
        timesheetRef != null
      ) {
        // Preserve the outgoing period before either clock-out path closes it.
        // The embedded flow may finish after midnight or after the next shift begins.
        // A retry must not erase this pin or replace it with a newer shift.
        dispatch(actionSetShiftHandoverTimesheetRef(timesheetRef));
      }
    } catch (err) {
      // Can't determine single vs multi -> fall back to the embedded flow, where
      // the operator can still clock out via the "All set!" button. Nothing has
      // failed from the operator's perspective, so no error toast.
      setIsClockingOut(false);
      openClockOutFlow();
      return;
    }

    if (hasOtherOpenWork) {
      setIsClockingOut(false);
      openClockOutFlow();
      return;
    }

    try {
      await client.mutate({
        mutation: CLOCK_OUT_OPERATOR,
        variables: { input: { personId } },
        fetchPolicy: 'no-cache',
      });
      throwSuccessToast(t('Clocked out for the day'));
      handleClose();
      // Single-ticket clock-out skips the embed gauntlet (where handover normally
      // opens), so on shift-handover machines open the handover drawer here. The
      // clock-out already closed the timesheet, so handover is presentational.
      if (shiftHandoverEnabled) {
        dispatch(actionOpenShiftHandover());
      }
    } catch (err) {
      // Clock-out call failed. Surface it and leave the modal open so the
      // operator can retry, rather than dropping them into the gauntlet.
      throwErrorToast(t('Failed to clock out. Please try again.'));
    } finally {
      setIsClockingOut(false);
    }
  };

  return (
    <ModalOverlay>
      <ModalContainer>
        <ModalHeader>
          <HeaderText>{t('Clock out for the day?')}</HeaderText>
        </ModalHeader>
        <ButtonContainer>
          <Button onClick={handleClockOut} disabled={isClockingOut}>
            {t('Yes')}
          </Button>
          <Button onClick={handleClose} disabled={isClockingOut}>
            {t('No')}
          </Button>
        </ButtonContainer>
      </ModalContainer>
    </ModalOverlay>
  );
});
